import _ from 'lodash';
import classNames from 'classnames';

import React, { useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Listing, User, Shooting } from 'models';
import { useAuth, useAlert, useGoogleAPIServices } from 'hooks';
import { ShootingContext } from 'contexts';
import { checkServiceability, fetchShootingParams } from 'apis';
import { ALERT, COLOR, CONDO_MIN_ROOMS, HOUSE_MIN_ROOMS, SHOOTING_STEPS } from 'config/constants';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import SaveRoundedIcon from '@material-ui/icons/SaveRounded';
import {
  IOption,
  IPlaceDetails,
  ICanadaPlaceDetails,
  InputChangeEvent,
  IPropertyType,
} from 'types';
import { HvAutoComplete, HvPlacesAutoComplete, HvGoogleMap, HvPleaseWait } from 'components';
import { images } from 'assets/images';

const useStyles = makeStyles({
  brokers: {
    marginBottom: 10,
  },
  label: {
    width: 70,
    fontFamily: 'FordAntenna-Medium',
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  addressBox: {
    flex: 1,
    position: 'relative',
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.23)',
    padding: '5px 40px 5px 15px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  address: {
    flex: 1,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  clearAddress: {
    position: 'absolute',
    top: 8,
    right: 10,
    color: 'gray',
    marginLeft: 10,
    cursor: 'pointer',
  },
  detailsBox: {
    marginTop: 30,
  },
  propertyTypes: {
    flexWrap: 'wrap',
    marginTop: 30,
    marginBottom: 30,
    justifyContent: 'flex-end',
  },
  button: {
    width: '100%',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'capitalize',
    '&.MuiButton-containedSecondary': {
      color: COLOR.WHITE,
    },
  },
  saveButton: {
    color: COLOR.WHITE,
    '@media (max-width: 767px)': {
      fontSize: 12,
    },
  },
  backdrop: {
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: COLOR.WHITE,
  },
  locationContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  locationInputs: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
  },
  image: {
    height: 500,
    width: '100%',
    objectFit: 'contain',
    borderRadius: 10,
    '@media (max-width: 768px)': {
      height: 300,
    },
    '@media (max-width: 500px)': {
      display: 'none',
    },
  },
  backButton: {
    backgroundColor: COLOR.WHITE,
    color: COLOR.LIGHT_BLUE,
    borderColor: COLOR.LIGHT_BLUE,
    marginRight: 20,
    '@media (max-width: 767px)': {
      fontSize: 10,
      marginRight: 5,
    },
  },
  title: {
    fontFamily: 'FordAntenna-Medium',
    fontSize: 30,
    marginBottom: 20,
    '@media (max-width: 767px)': {
      fontSize: 20,
    },
  },
  block: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 15,
  },
  item: {
    width: '100%',
  },
  buttons: {
    '@media (max-width: 767px)': {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  buttonText: {
    '@media (max-width: 767px)': {
      display: 'none',
    },
  },
});

interface Props {
  setActiveStep: (_: number) => void;
  onCancel: () => void;
}

function LocationStep({ setActiveStep, onCancel }: Props) {
  const intl = useIntl();
  const styles = useStyles();

  const { user, token } = useAuth();
  const { displayAlert } = useAlert();

  const {
    brokers,
    listing,
    shooting,
    isEditMode,
    addressFound,
    isServicable,
    availabilities,
    setAmenities,
    setPackages,
    setDistanceFee,
    setLongDistanceServiceOffer,
    setServiceOffers,
    setIsServicable,
    setAvailabilities,
    setAddressFound,
    updateListingFields,
    updateShootingFields,
    saveShooting,
    resetContext,
    validateLocationStep,
  } = useContext(ShootingContext);

  const isDraftMode = shooting.state === Shooting.STATE_ENUM.draft;
  const [brokerName, setBrokerName] = useState('');
  const [loading, setLoading] = useState(false);
  const { isGoogleAPILoaded } = useGoogleAPIServices();
  const fmtApiError = intl.formatMessage({ id: 'shooting.create.messages.apiError' });
  const fmtUnserviceable = intl.formatMessage({ id: 'shooting.create.messages.unserviceable' });
  const fmtLoading = intl.formatMessage({ id: 'shooting.create.messages.loading' });
  const fmtBackToShootings = intl.formatMessage({ id: 'shooting.create.buttons.backToShootings' });
  const fmtBack = intl.formatMessage({ id: 'shooting.create.buttons.back' });
  const fmtBookAppointment = intl.formatMessage({ id: 'shooting.create.bookShooting' });
  const fmtSaveDraft = intl.formatMessage({ id: 'shooting.create.buttons.saveDraft' });
  const fmtSave = intl.formatMessage({ id: 'shooting.create.buttons.save' });
  const fmtInputNumber = intl.formatMessage({ id: 'shooting.create.labels.inputNumber' });

  const loadShootingParams = async () => {
    setLoading(true);
    if (!token || !listing.lat || !listing.lng) {
      setLoading(false);
      return;
    }

    const _isServicable = await checkServiceability(token, listing.lat, listing.lng);
    setIsServicable(_isServicable?.is_serviceable);
    setAvailabilities(_isServicable.availabilities || []);
    if (_isServicable?.is_serviceable) {
      const _brokerId = user.isAdmin ? listing.broker_id || user.id : user.id;
      const res = await fetchShootingParams(token, _brokerId, listing.lat, listing.lng);
      if (!_.isEmpty(res)) {
        const { distanceFee, amenities, packages, serviceOffers, longDistanceServiceOffer } = res;
        setAmenities(amenities);
        setPackages(packages);
        setDistanceFee(distanceFee);
        setServiceOffers(serviceOffers);
        setLongDistanceServiceOffer(longDistanceServiceOffer);

        if (!isEditMode || isDraftMode) {
          updateShootingFields(distanceFee);
        }
      } else {
        displayAlert(ALERT.ERROR, fmtApiError);
        setIsServicable(false);
      }
    } else {
      displayAlert(ALERT.ERROR, fmtUnserviceable);
    }
    setLoading(false);
  };
  useEffect(() => {
    const foundExistingBroker = brokers.find((m: User) => {
      return m.id === listing.broker_id;
    });
    if (foundExistingBroker && foundExistingBroker.fullName) {
      setBrokerName(foundExistingBroker.fullName);
    }
  }, [listing.broker_id, brokers]);

  useEffect(() => {
    if (listing.broker_id) {
      loadShootingParams();
    }
  }, [listing.broker_id]);

  useEffect(() => {
    if (!isEditMode && addressFound) {
      loadShootingParams();
    }
  }, [addressFound]);

  const onBrokerChange = (o: IOption) => {
    updateListingFields({ broker_id: Number(o.value) });
  };

  const onLocationChange = (o: IOption, pd: IPlaceDetails | ICanadaPlaceDetails, type: string) => {
    const locationInfo = Listing.parseAddressFromDetails(o.label, pd, type);
    if (locationInfo) {
      updateListingFields(locationInfo);
    }
    setAddressFound(!!locationInfo);
  };

  const onCivicNumberChange = (e: InputChangeEvent) => {
    const _civicNumber = e.target.value;
    updateListingFields({
      civic_number: _civicNumber,
      address: `${_civicNumber} ${listing.street}`,
    });
  };

  const onPostalCodeChange = (e: InputChangeEvent) => {
    updateListingFields({ postalcode: e.target.value });
  };

  const onAppartmentChange = (e: InputChangeEvent) => {
    updateListingFields({ app: e.target.value });
  };

  const onPlexUnitCountChange = (e: InputChangeEvent) => {
    updateShootingFields({ plex_unit_count: e.target.value });
    const input = e.target.value;
    if (input === '' || /^[1-9][0-9]*$/.test(input) || input === '0') {
      updateListingFields({ plex_unit_count: e.target.value });
    } else if (input === '') {
      updateListingFields({ plex_unit_count: '' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const onPropertyTypeChange = (type: number) => {
    const isCondo = type === Listing.TYPE_ENUM.CONDO;
    const roomCount = isCondo ? CONDO_MIN_ROOMS : HOUSE_MIN_ROOMS;

    updateListingFields({ property_type: type, app: '' });
    updateShootingFields({
      plex_unit_count: null,
    });
  };

  const onUpdate = () => {
    if (validateLocationStep()) {
      saveShooting();
    }
  };

  const onNextStep = () => {
    if (validateLocationStep()) {
      setActiveStep(SHOOTING_STEPS.PROPERTY);
    }
  };

  const options: IOption[] = brokers.map((m: User) => {
    return { value: m.id, label: m.fullName };
  });

  const renderLocationInput = () => {
    if (!isGoogleAPILoaded) {
      return <HvPleaseWait />;
    }
    if (!isEditMode && !addressFound && isGoogleAPILoaded) {
      return (
        <Grid container spacing={1} className={styles.locationContent}>
          <Grid item md={5} sm={12} className={styles.locationInputs}>
            <Typography className={styles.title}>{fmtBookAppointment}</Typography>
            {user.isAdmin && (
              <HvAutoComplete
                className={styles.brokers}
                label={intl.formatMessage({ id: 'shooting.create.labels.broker' })}
                placeholder={intl.formatMessage({ id: 'shooting.create.labels.broker' })}
                hideArrow
                inputValue={brokerName}
                options={options}
                onChange={onBrokerChange}
                onInputChange={setBrokerName}
              />
            )}
            <HvPlacesAutoComplete
              token={token}
              hideArrow
              label={intl.formatMessage({ id: 'shooting.create.labels.address' })}
              inputValue={listing.fullAddress || ''}
              onChange={onLocationChange}
            />
          </Grid>
          <Grid item md={6} sm={12}>
            <img src={images.calendar_example} className={styles.image} alt='location' />
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  return (
    <>
      <Box className='ScrollView'>
        {user.isAdmin && addressFound ? (
          <HvAutoComplete
            className={styles.brokers}
            label={intl.formatMessage({ id: 'shooting.create.labels.broker' })}
            placeholder={intl.formatMessage({ id: 'shooting.create.labels.broker' })}
            hideArrow
            inputValue={brokerName}
            options={options}
            onChange={onBrokerChange}
            onInputChange={setBrokerName}
          />
        ) : null}
        {renderLocationInput()}

        {!isEditMode && addressFound ? (
          <Grid container className={styles.block}>
            <Grid item md={2} sm={12}>
              <Typography className={styles.label}>
                {intl.formatMessage({ id: 'shooting.create.labels.address' })}
              </Typography>
            </Grid>
            <Grid item md={10} sm={12}>
              <div className={classNames('FlexBox AlignCenter NoMargin', styles.addressBox)}>
                <Typography>{listing.fullAddress}</Typography>
                <CloseIcon className={styles.clearAddress} onClick={resetContext} />
              </div>
            </Grid>
          </Grid>
        ) : null}

        {addressFound && isServicable ? (
          <Box className={styles.detailsBox}>
            <h2>{intl.formatMessage({ id: 'shooting.create.titles.location' })}</h2>

            <Grid container className={styles.block}>
              <Grid item md={2} sm={12}>
                <Typography className='InfoItem_Label'>
                  {intl.formatMessage({ id: 'address' })}
                </Typography>
              </Grid>
              <Grid item md={10} sm={12}>
                <Typography className='InfoItem_Value'>
                  {listing.cityAddressWithPostalCode}
                </Typography>
              </Grid>
            </Grid>

            <Grid container className={styles.block}>
              <Grid item md={2} sm={12}>
                <Typography className='InfoItem_Label'>
                  {intl.formatMessage({ id: 'shooting.create.labels.civicNumber' })}
                </Typography>
              </Grid>
              <Grid item md={10} sm={12} className={styles.item}>
                <TextField
                  className={classNames('InfoItem_Value', styles.item)}
                  variant='outlined'
                  fullWidth
                  value={listing.civic_number}
                  onChange={onCivicNumberChange}
                  inputProps={{ readOnly: isEditMode && !isDraftMode }}
                />
              </Grid>
            </Grid>

            <Grid container className={styles.block}>
              <Grid item md={2} sm={12}>
                <Typography className='InfoItem_Label'>
                  {intl.formatMessage({ id: 'shooting.create.labels.postalCode' })}
                </Typography>
              </Grid>
              <Grid item md={10} sm={12} className={styles.item}>
                <TextField
                  className={classNames('InfoItem_Value', styles.item)}
                  variant='outlined'
                  fullWidth
                  value={listing.postalcode}
                  onChange={onPostalCodeChange}
                  inputProps={{ readOnly: isEditMode && !isDraftMode }}
                />
              </Grid>
            </Grid>

            {listing.property_type !== Listing.TYPE_ENUM.HOUSE ? (
              <Grid container className={styles.block}>
                <Grid item md={2} sm={12}>
                  <Typography className='InfoItem_Label'>
                    {listing.property_type === Listing.TYPE_ENUM.COMMERCIAL
                      ? intl.formatMessage({ id: 'shooting.create.labels.suite' })
                      : intl.formatMessage({ id: 'shooting.create.labels.appartement' })}
                  </Typography>
                </Grid>
                <Grid item md={10} sm={12} className={styles.item}>
                  <TextField
                    className={classNames('InfoItem_Value', styles.item)}
                    variant='outlined'
                    value={listing.app}
                    fullWidth
                    onChange={onAppartmentChange}
                  />
                </Grid>
              </Grid>
            ) : null}

            {listing.property_type === Listing.TYPE_ENUM.PLEX ? (
              <Grid container className={styles.block}>
                <Grid item md={2} sm={12}>
                  <Typography className='InfoItem_Label'>
                    {intl.formatMessage({ id: 'shooting.create.labels.plexUnitCount' })}
                  </Typography>
                </Grid>
                <Grid item md={10} sm={12} className={styles.item}>
                  <TextField
                    className={classNames('InfoItem_Value', styles.item)}
                    variant='outlined'
                    fullWidth
                    type='number'
                    onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                      if ((e.nativeEvent as InputEvent).data === '0') {
                        e.preventDefault();
                      }
                    }}
                    helperText={fmtInputNumber}
                    inputProps={{ min: 1, inputMode: 'numeric', pattern: '^[0-9]*$' }}
                    value={shooting.plex_unit_count}
                    onChange={onPlexUnitCountChange}
                    onKeyDown={handleKeyDown}
                  />
                </Grid>
              </Grid>
            ) : null}

            <Grid container className={styles.propertyTypes}>
              <Grid item md={10} sm={12}>
                <Grid container spacing={3}>
                  {Listing.TYPES.map((m: IPropertyType) => {
                    return (
                      <Grid item md={3} sm={6} xs={6} key={m.type}>
                        <Button
                          key={m.type}
                          className={styles.button}
                          variant='contained'
                          disabled={isEditMode && !isDraftMode}
                          color={listing.property_type === m.type ? 'secondary' : 'default'}
                          onClick={() => {
                            return onPropertyTypeChange(m.type);
                          }}
                        >
                          {intl.formatMessage({ id: `shooting.create.labels.${m.name}` })}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>

            {!!listing.lat && !!listing.lng ? (
              <HvGoogleMap lat={Number(listing.lat)} lng={Number(listing.lng)} />
            ) : null}

            {isEditMode && shooting?.reshoot_note ? (
              <div style={{ marginTop: 20 }}>
                <Typography className='InfoItem_Label'>
                  {intl.formatMessage({ id: 'shooting.create.labels.techNote' })}
                </Typography>
                <Typography style={{ fontSize: 16 }}>{shooting.reshoot_note}</Typography>
              </div>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Box className='ButtonsBox'>
        <div className={styles.buttons}>
          <Button variant='outlined' className={styles.backButton} onClick={onCancel}>
            <ChevronLeftRoundedIcon />
            {addressFound ? fmtBack : fmtBackToShootings}
          </Button>
          {addressFound && isServicable && (
            <Button
              className={styles.saveButton}
              variant='contained'
              color='secondary'
              onClick={onUpdate}
            >
              <SaveRoundedIcon />
              <Typography className={styles.buttonText}>
                {isDraftMode || !shooting.id ? fmtSaveDraft : fmtSave}
              </Typography>
            </Button>
          )}
        </div>
        {addressFound && isServicable && !loading ? (
          <Button
            variant='contained'
            color='secondary'
            className={styles.saveButton}
            onClick={onNextStep}
          >
            {`${intl.formatMessage({ id: 'shooting.create.buttons.next' })} (1/5)`}
          </Button>
        ) : null}
        {loading ? (
          <Backdrop open={loading} className={styles.backdrop}>
            <Typography>{fmtLoading}</Typography>
            <CircularProgress size={30} disableShrink color='primary' thickness={2} />
          </Backdrop>
        ) : null}
      </Box>
    </>
  );
}

export default LocationStep;
